import type { Flags } from '@fullstory/client-api';
import { PageModel } from '@fullstory/playback-model';
import { useAsyncEffect } from '@fullstory/react-hooks';
import { useLayoutSubscription } from '@fullstory/react-rxjs';
import type { PlayResult } from '@fullstory/renderer-shared';
import { once } from 'lodash';
import { useRef, useState } from 'react';
import { Observable } from 'rxjs';
import { createRendererClient, LoadResult, RendererClient } from './createRendererClient';
import { getRendererURL } from './getRendererURL';
import { registerPlaybackServiceWorker } from './registerPlaybackServiceWorker';

const registerServiceWorkerOnce = once(registerPlaybackServiceWorker);

export type LoadEndResult =
  | { succeeded: true; canceled: false; error: undefined; result: LoadResult }
  | { succeeded: false; canceled: true; error: undefined; result: undefined }
  | { succeeded: false; canceled: false; error: Error; result: undefined };

export type { PlayResult } from '@fullstory/renderer-shared';

export type PlayEndResult =
  | { succeeded: true; canceled: false; error: undefined; result: PlayResult }
  | { succeeded: false; canceled: true; error: undefined; result: undefined }
  | { succeeded: false; canceled: false; error: Error; result: undefined };

interface UseClientParams {
  flags: Flags;
  iframe?: HTMLIFrameElement | null;
  orgId: string;
  offsetMs$: Observable<number | undefined>;
  page?: Readonly<PageModel> | null;
}

interface UseClientOptions {
  onLoad?: (client: RendererClient, result: LoadResult) => void;
  onLoadEnd?: (result: LoadEndResult) => void;
  onLoadError?: (e: Error) => void;
  onLoadStart?: () => void;
  onPlay?: (result: PlayResult) => void;
  onPlayEnd?: (result: PlayEndResult) => void;
  onPlayError?: (e: Error) => void;
  onPlayStart?: (offsetMs: number) => void;
}

/**
 * Creates a new RendererClient and preloads the page
 */
export const useClient = (
  params: UseClientParams,
  options: UseClientOptions = {},
): RendererClient | undefined => {
  const { flags, iframe, offsetMs$, orgId, page } = params;
  const {
    onLoad = () => {},
    onLoadEnd = () => {},
    onLoadError = () => {},
    onLoadStart = () => {},
    onPlay = () => {},
    onPlayEnd = () => {},
    onPlayError = () => {},
    onPlayStart = () => {},
  } = options;

  // Maintain an up-to-date ref for the offset so that it can be accessed synchronously.
  // It's critical to omit the offset from deps because we _never_ want a changing offset
  // pointer to cause the client to reload. It's just factored into the _initial_ load.
  const offsetMsRef = useRef<number>();
  useLayoutSubscription(offsetMs$, offsetMs => {
    offsetMsRef.current = offsetMs;
  });

  const [client, setClient] = useState<RendererClient>();
  // This effect is responsible for initializing a new client whenever relevant
  // params change. It will also invoke any provided lifecycle callbacks.
  useAsyncEffect(
    async isActive => {
      // Alias maybe to be a bit less repetitive down below
      const attempt = <T>(fn: () => T, onCancel?: () => {}) => maybe(fn, isActive, onCancel);

      // Return early if we're missing any required params
      if (!flags || !iframe || !orgId || !page) return;

      // Ensure we have the fully cooked page
      if (flags.featureFlags().AppPlaybackLazyCookedPageFetching) {
        await page.getCookedPage();
      }

      // Notify that loading started
      let didLoad = false;
      onLoadStart();

      try {
        // Register the service worker at least once
        await attempt(registerServiceWorkerOnce);

        // Load the client
        const url = await attempt(() => getRendererURL({ flags, orgId, page }));
        const nextClient = await attempt(() => createRendererClient({ iframe, url, page }));
        const load = () => nextClient.load();
        const teardown = () => nextClient.teardown();
        const loadResult = await attempt(load, teardown);

        // Notify that loading was successful
        attempt(() => {
          onLoadEnd?.({ succeeded: true, canceled: false, error: undefined, result: loadResult });
          onLoad?.(nextClient, loadResult);
          didLoad = true;
        }, teardown);

        // Determine the initial offset and notify that playback is starting
        const getOffset = async () => offsetMsRef.current ?? (await page.getFirstInsertWhen()) ?? 0;
        const offsetMs = await attempt(getOffset, teardown);
        attempt(() => onPlayStart(offsetMs), teardown);

        // Play to the initial timestamp
        const play = () => nextClient.play({ offsetMs });
        const playResult = await attempt(play, teardown);

        // Notify that playback was successful and finally update the client state.
        // We do this step last to avoid committing a stale or partially initialized
        // client to state.
        attempt(() => {
          onPlayEnd({ succeeded: true, canceled: false, error: undefined, result: playResult });
          onPlay(playResult);
          setClient(prev => {
            prev?.teardown();
            return nextClient;
          });
        }, teardown);

        // Return a cleanup function to perform before the next effect runs.
        return teardown;
      } catch (e: any) {
        // We don't need to report the error if the effect was just canceled.
        if (e instanceof CancelError || e.isCancelError) {
          const cb = didLoad ? onPlayEnd : onLoadEnd;
          cb?.({ succeeded: false, canceled: true, error: undefined, result: undefined });
          return;
        }

        if (didLoad) {
          onPlayEnd({ succeeded: false, canceled: false, error: e, result: undefined });
          onPlayError(e);
        } else {
          onLoadError?.(e);
          onLoadEnd?.({ succeeded: false, canceled: false, error: e, result: undefined });
        }

        return () => {};
      }
    },
    [flags, iframe, orgId, page],
  );

  return client;
};

class CancelError extends Error {
  constructor() {
    super('Canceled');
    Object.setPrototypeOf(this, CancelError.prototype);
  }

  isCancelError = true;
}

function maybe<T>(fn: () => T, isActive: () => boolean, onCancel?: () => void): T {
  if (!isActive()) {
    onCancel?.();
    throw new CancelError();
  }

  return fn();
}
